/**
 * Masternode Collateral for the Nexus application.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/masternodes/collateral.jsx
 * @class SubPageMasterNodes
 * @since 0.3.7
*/

import React, {useState} from 'react';
import Web3 from 'web3';
import energiExtension from '@energi/web3-ext';
import {useStore} from 'effector-react';
import {TextField} from '@rmwc/textfield';
import {Button} from '@rmwc/button';
import {CircularProgress} from '@rmwc/circular-progress';
import {Elevation} from '@rmwc/elevation';
import {Snackbar, SnackbarAction} from '@rmwc/snackbar';
import Title from '../../Title';
import masternodeOwnerStore from '../../../globals/masternode-owner';
import metamaskStore from '../../../globals/metamask';
import masternodeOperationTx from '../../../globals/masternode-operation-tx';
import formatNumber from '../../../lib/helpers/bn-format';
import errorMessages from '../../../lib/constants/error-messages';
import {networkOptions} from '../../../lib/constants/network-info';
import langNavigate from '../../../lib/helpers/lang-navigate';
import translations from '../../../lib/translations';
import {statusTextToI18nKey} from '../../../lib/helpers/masternode-status-text';
import Footer from '../../Footer';
import disabledDeposit from '../../../images/deposit-disabled.svg';
import disabledWithdraw from '../../../images/withdraw-disabled.svg';
import imgDeposit from '../../../images/deposit-active.svg';
import imgWithdraw from '../../../images/withdraw-active.svg';

const web3 = new Web3(networkOptions.networkAddress);

energiExtension.extend(web3);

const MasternodeCollateral = props => {
    const [errorMessage, setErrorMessage] = useState(''),
        masternodeOwner = useStore(masternodeOwnerStore.$store),
        operationTx = useStore(masternodeOperationTx.$txOwnerAddress),
        correctNetwork = useStore(metamaskStore.$correctNetwork),
        metamaskConnected = useStore(metamaskStore.$connected),
        translate = translations.init(props),
        locales = props.langInfo && props.langInfo.locales,
        langInfo = props.langInfo;

    let masternodeOwnerAddressField;

    const displayBalance = () => {
        let pollingNotification = null;

        if (operationTx) {
            pollingNotification = <Button icon={<CircularProgress />} label={translate.t('TRANSACTION_CHECK', true)} />;
        }

        if (masternodeOwner.validAddress) {
            const total = masternodeOwner.collateral.add(masternodeOwner.balance);

            return (
                <div>
                    <br />
                    <div className="elevation">
                        <Elevation>
                            <small>{translate.t('ACCOUNT_BALANCE', true)}</small>
                            <p>{formatNumber(locales, masternodeOwner.balance, 1)} NRG</p>
                        </Elevation>
                        <Elevation>
                            <small>{translate.t('MASTERNODE', true)} {translate.t('COLLATERAL')}</small>
                            <p>{formatNumber(locales, masternodeOwner.collateral, 0)} NRG</p>
                        </Elevation>
                        <Elevation>
                            <small>{translate.t('TOTAL_ACCOUNT_BALANCE', true)}</small>
                            <p>{formatNumber(locales, total, 1)} NRG</p>
                        </Elevation>
                        <Elevation>
                            <small>{translate.t('MASTERNODE', true)} {translate.t('LISTING')}</small>
                            <p className={`status status-${masternodeOwner.masternodeStatus}`}>{translate.t(statusTextToI18nKey(masternodeOwner.masternodeStatus))}</p>
                        </Elevation>
                    </div>
                    <br />
                    {pollingNotification}
                </div>
            );
        }
    };

    /**
    * Changes the Masternode Owner global state address.
    * If a user enters a Masterndoe Address, then a message is shown and the related Owner address
    * will be processed.
    *
    * @method changeAddress
    * @param e {Object} event object
    * @since 0.0.1
    */
    const changeAddress = async e => {
        let masternodeOwnerAddress = e.target.value,
            masternodeInfo;

        if (web3.utils.isAddress(masternodeOwnerAddress)) {
            try {
                masternodeInfo = await web3.masternode.masternodeInfo(masternodeOwnerAddress); // lookup
            }
            catch (err) {
                console.error(err);
            }
        }

        if (masternodeInfo) {
            // check if user did pass a masternode address instead of owner address
            if (masternodeOwnerAddress === masternodeInfo.masternode) {
                masternodeOwnerAddress = masternodeInfo.owner;
                // show message
                setErrorMessage(errorMessages.MASTERNODE_ADDRESS_INSTEAD_OF_OWNER_ADDRESS);
            }
        }
        masternodeOwnerStore.on.changeAddress(masternodeOwnerAddress);
    };

    let withdraw,
        deposit;
    deposit = !masternodeOwner.canDeposit || operationTx ? disabledDeposit : imgDeposit;
    withdraw = !masternodeOwner.canWithdraw || operationTx ? disabledWithdraw : imgWithdraw;

    if (!correctNetwork || !metamaskConnected) {
        masternodeOwnerAddressField = (
            <TextField
                className="input"
                label={translate.t('MASTERNODE_OWNER', true)}
                onChange={changeAddress}
                outlined
                value={masternodeOwner.address} />
        );
    }

    return (
        <div className="masternodes pure-u subpage">
            <Title subTitle={translate.md('masternodes-collateral')} title={translate.t('COLLATERAL')} />
            <section className="collateral">
                <div className="input-wrapper">
                    {masternodeOwnerAddressField}
                    {displayBalance()}

                    <div className="action-buttons">
                        <Button
                            className="depositButton"
                            disabled={!masternodeOwner.canDeposit || operationTx}
                            icon={deposit}
                            label={translate.t('DEPOSIT', true) + ' ' + translate.t('COLLATERAL')}
                            onClick={() => langNavigate(langInfo, '/masternodes/collateral/deposit')}
                            raised
                            ripple={false} />
                        <Button
                            className="withdrawButton"
                            disabled={!masternodeOwner.canWithdraw || operationTx}
                            icon={withdraw}
                            label={translate.t('WITHDRAW', true) + ' ' + translate.t('COLLATERAL')}
                            onClick={() => langNavigate(langInfo, '/masternodes/collateral/withdraw')}
                            raised
                            ripple={false} />
                    </div>
                </div>
                <Snackbar
                    action = {
                        <SnackbarAction
                            label={translate.t('CLOSE')}
                            onClick={() => setErrorMessage('')} />
                    }
                    message={errorMessage}
                    onClose={() => setErrorMessage('')}
                    open={!!errorMessage} />
            </section>
            <Footer {...props} />
        </div>
    );
};

export default MasternodeCollateral;
